/**
 * ExtranetHome — lista de espaços disponíveis para o usuário externo.
 */
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useExtranetSpaces } from '@/hooks/kb/useKbExtranet';
import { useExtranetAuth } from '@/hooks/useExtranetAuth';
import { Card } from '@/components/ui/card';
import { Loader2, FolderOpen, FileText } from 'lucide-react';

export default function ExtranetHome() {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const { tenant } = useExtranetAuth();
  const { data: spaces, isLoading } = useExtranetSpaces(tenantSlug);

  return (
    <div className="space-y-5">
      <Helmet>
        <title>Espaços — {tenant?.name ?? 'Portal do Cliente'}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Espaços</h1>
        <p className="text-sm text-muted-foreground">
          Conteúdos liberados para o seu acesso.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !spaces || spaces.length === 0 ? (
        <Card className="p-10 text-center text-sm text-muted-foreground">
          Ainda não há nenhum espaço liberado para você. Fale com o administrador
          da organização.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {spaces.map((s: any) => (
            <Link
              key={s.space_id}
              to={`/extranet/${tenantSlug}/espacos/${s.slug}`}
              className="group"
            >
              <Card className="p-4 h-full transition-shadow group-hover:shadow-md">
                <div className="flex items-start gap-3">
                  <div
                    className="h-10 w-10 rounded flex items-center justify-center shrink-0"
                    style={{ backgroundColor: s.color ? `${s.color}22` : 'hsl(var(--primary) / 0.1)' }}
                  >
                    <FolderOpen className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{s.name}</div>
                    {s.description && (
                      <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {s.description}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <FileText className="h-3 w-3" />
                      {Number(s.pages_count ?? 0)} páginas
                    </div>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
